import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChatResponse,
  ChatTurn,
  PublicCreatorChatConfig,
  creatorChatPublicService,
  getCreatorChatVisitorId,
  matchCreatorChatActionVideo,
  pollChatVideoJob,
  preloadCreatorChatVideos,
  getCreatorChatActionVideoPlayState,
  warmCreatorChatVideoOnGesture,
  isCreatorChatVideoHot,
} from '../services/creatorChatService';
import {
  playCreatorChatAudioUrl,
  stopCreatorChatAudio,
  unlockCreatorChatAudioGesture,
  isCreatorChatAudioBusy,
  type CreatorChatAudioRefs,
} from '../services/creatorChatAudio';
import {
  startCreatorChatProactiveSchedule,
  type ProactiveNudgeKind,
} from '../services/creatorChatProactive';
import '../components/admin/AdminComponents.css';

type TriggerRingItem = {
  id: number;
  label: string;
  fullLabel: string;
  videoUrl: string;
};

const TRIGGER_RING_COLORS = [
  '#086afb',
  '#0dca9d',
  '#e7038e',
  '#fbb445',
  '#7c3aed',
  '#06b6d4',
  '#f97316',
  '#64748b',
];

function primaryTriggerPhrase(action: {
  synonyms?: string | null;
  label?: string | null;
  actionSlug?: string | null;
}): string {
  const fromSynonyms = (action.synonyms || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (fromSynonyms[0]) return fromSynonyms[0];
  if (action.label && action.label.trim()) return action.label.trim();
  return (action.actionSlug || 'clip').replace(/_/g, ' ');
}

function truncateTriggerLabel(text: string, max = 14): string {
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function polarPoint(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function donutSlicePath(
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
  startDeg: number,
  endDeg: number
): string {
  const sweep = endDeg - startDeg;
  const large = sweep > 180 ? 1 : 0;
  const p0 = polarPoint(cx, cy, outerR, startDeg);
  const p1 = polarPoint(cx, cy, outerR, endDeg);
  const p2 = polarPoint(cx, cy, innerR, endDeg);
  const p3 = polarPoint(cx, cy, innerR, startDeg);
  return [
    `M ${p0.x} ${p0.y}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${p1.x} ${p1.y}`,
    `L ${p2.x} ${p2.y}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${p3.x} ${p3.y}`,
    'Z',
  ].join(' ');
}

const pageShell: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 12,
  boxSizing: 'border-box',
};

const embeddedShell: React.CSSProperties = {
  minHeight: 'calc(100vh - 80px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '16px 12px 32px',
  boxSizing: 'border-box',
  background: '#f5f4f4',
};

type CreatorChatWidgetPageProps = {
  /** When true, renders inside the retail banking shell with a back link to the catalog. */
  embedded?: boolean;
};

const CreatorChatWidgetPage: React.FC<CreatorChatWidgetPageProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { publicId = '' } = useParams<{ publicId: string }>();
  const [config, setConfig] = useState<PublicCreatorChatConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [history, setHistory] = useState<ChatTurn[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionVideoUrl, setActionVideoUrl] = useState<string | null>(null);
  const [showIdle, setShowIdle] = useState(true);
  const [videoLoading, setVideoLoading] = useState(false);
  const [generatingClip, setGeneratingClip] = useState(false);
  const [idleReady, setIdleReady] = useState(false);
  const [actionReady, setActionReady] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [pendingAudioUrl, setPendingAudioUrl] = useState<string | null>(null);
  const [ringTriggerId, setRingTriggerId] = useState<number | null>(null);
  const playedUrlRef = useRef<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const audioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const audioGenRef = useRef(0);
  const audioQueueRef = useRef<string[]>([]);
  const audioPlayingRef = useRef(false);
  const audioIdleWaitersRef = useRef<Array<() => void>>([]);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const audioRefs = (): CreatorChatAudioRefs => ({
    ctxRef: audioCtxRef,
    htmlAudioRef: audioRef,
    sourceRef: audioSourceRef,
    generationRef: audioGenRef,
    queueRef: audioQueueRef,
    playingRef: audioPlayingRef,
    idleWaitersRef: audioIdleWaitersRef,
  });

  const actionVideoRef = useRef<HTMLVideoElement>(null);
  const idleVideoRef = useRef<HTMLVideoElement>(null);
  const nudgeBusyRef = useRef(false);
  const busyRef = useRef(false);
  const historyRef = useRef<ChatTurn[]>([]);
  const visitorIdRef = useRef('');
  const openRef = useRef(true);

  historyRef.current = history;
  busyRef.current = busy;
  openRef.current = open;

  const stopChatPlayback = () => {
    stopCreatorChatAudio(audioRefs());
    setPendingAudioUrl(null);
    setAudioBlocked(false);
    try {
      idleVideoRef.current?.pause();
      actionVideoRef.current?.pause();
    } catch {
      /* ignore */
    }
  };

  const closeChat = () => {
    stopChatPlayback();
    if (embedded) {
      navigate('/creator-chat');
      return;
    }
    setOpen(false);
  };

  const shellStyle = embedded ? embeddedShell : pageShell;
  const shellBackground = embedded
    ? undefined
    : 'linear-gradient(160deg, #0f172a 0%, #1e293b 45%, #334155 100%)';

  const accent = config?.accentColor || '#086afb';
  const left = config?.launcherPosition === 'BOTTOM_LEFT';
  const videoEnabled = !!config?.actionsEnabled;
  const voiceEnabled = !!config?.voiceEnabled;

  useEffect(() => {
    const updateViewport = () => {
      const height = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty('--creator-chat-vvh', `${height}px`);
    };
    updateViewport();
    window.visualViewport?.addEventListener('resize', updateViewport);
    window.visualViewport?.addEventListener('scroll', updateViewport);
    window.addEventListener('resize', updateViewport);
    return () => {
      window.visualViewport?.removeEventListener('resize', updateViewport);
      window.visualViewport?.removeEventListener('scroll', updateViewport);
      window.removeEventListener('resize', updateViewport);
      document.documentElement.style.removeProperty('--creator-chat-vvh');
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const cfg = await creatorChatPublicService.getConfig(publicId);
        if (cancelled) return;
        setConfig(cfg);
        const visitorId = getCreatorChatVisitorId(publicId);
        visitorIdRef.current = visitorId;

        let initialHistory: ChatTurn[] = [];
        if (cfg.memoryEnabled !== false) {
          try {
            const mem = await creatorChatPublicService.getHistory(publicId, visitorId);
            if (mem.history && mem.history.length > 0) {
              initialHistory = mem.history;
            }
          } catch {
            /* fall back to welcome only */
          }
        }
        setHistory(initialHistory);

        preloadCreatorChatVideos([
          cfg.idleVideoUrl,
          cfg.avatarImageUrl,
          ...(cfg.actions || []).map((a) => a.videoUrl),
        ]);
      } catch {
        if (!cancelled) {
          setError('This chat is unavailable. It may be unpublished or the link is invalid.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [publicId]);

  useEffect(() => {
    if (!open) {
      stopChatPlayback();
    }
  }, [open]);

  useEffect(() => () => {
    stopCreatorChatAudio(audioRefs());
  }, []);

  useEffect(() => {
    if (!config || !open || loading || !publicId) return undefined;

    const runProactiveNudge = async (kind: ProactiveNudgeKind) => {
      if (nudgeBusyRef.current || busyRef.current) return;
      nudgeBusyRef.current = true;
      try {
        const res = await creatorChatPublicService.proactiveNudge(
          publicId,
          kind,
          historyRef.current,
          visitorIdRef.current
        );
        if (!openRef.current) return;
        if (res.reply) {
          setHistory((h) => [...h, { role: 'assistant', content: res.reply }]);
        }
        if (res.action?.videoUrl && openRef.current) {
          playActionVideo(res.action.videoUrl);
        }
        if (res.audioUrl && openRef.current) {
          playReplyAudio(res.audioUrl);
        }
      } catch {
        /* ignore — proactive is best-effort */
      } finally {
        nudgeBusyRef.current = false;
      }
    };

    return startCreatorChatProactiveSchedule({
      enabled: true,
      active: open && !loading,
      hasWelcome: false,
      isBusy: () =>
        busyRef.current || nudgeBusyRef.current || isCreatorChatAudioBusy(audioRefs()),
      onNudge: runProactiveNudge,
    });
  }, [config, open, loading, publicId]);

  useEffect(() => {
    if (!open || !config?.idleVideoUrl || !showIdle || actionVideoUrl) return undefined;
    const t = window.setTimeout(() => {
      void idleVideoRef.current?.play().catch(() => undefined);
    }, 80);
    return () => window.clearTimeout(t);
  }, [open, config?.idleVideoUrl, showIdle, actionVideoUrl]);

  useEffect(() => {
    const box = messagesRef.current;
    if (!box) return;
    requestAnimationFrame(() => {
      box.scrollTop = box.scrollHeight;
    });
  }, [history, busy, audioBlocked]);

  const title = useMemo(
    () => config?.title || config?.creatorLabel || 'Chat',
    [config]
  );

  const triggerRingItems = useMemo((): TriggerRingItem[] => {
    return (config?.actions || [])
      .filter(
        (a) =>
          a.showOnTriggerWheel !== false &&
          !!(a.videoUrl || '').trim() &&
          !(a.videoUrl || '').includes('example.com')
      )
      .map((a, index) => {
        const fullLabel = primaryTriggerPhrase(a);
        return {
          id: a.id ?? index + 1,
          fullLabel,
          label: truncateTriggerLabel(fullLabel),
          videoUrl: (a.videoUrl || '').trim(),
        };
      });
  }, [config?.actions]);

  const wheelCenterItem = useMemo((): TriggerRingItem | null => {
    const actions = config?.actions || [];
    const centerIndex = actions.findIndex(
      (a) =>
        a.triggerWheelCenter === true &&
        !!(a.videoUrl || '').trim() &&
        !(a.videoUrl || '').includes('example.com')
    );
    if (centerIndex < 0) return null;
    const center = actions[centerIndex];
    const fullLabel =
      (center.label && center.label.trim()) ||
      primaryTriggerPhrase(center) ||
      'Special';
    return {
      id: center.id ?? centerIndex + 1000,
      fullLabel,
      label: truncateTriggerLabel(fullLabel, 10),
      videoUrl: (center.videoUrl || '').trim(),
    };
  }, [config?.actions]);

  const wheelVisible = videoEnabled && (triggerRingItems.length > 0 || !!wheelCenterItem);

  const playActionVideo = (url: string, triggerId?: number | null) => {
    if (!url) return;
    playedUrlRef.current = url;
    preloadCreatorChatVideos([url]);
    const { ready, loading } = getCreatorChatActionVideoPlayState(url);
    setShowIdle(false);
    setActionReady(ready);
    setVideoLoading(loading);
    setActionVideoUrl(url);
    setRingTriggerId(triggerId ?? null);
  };

  const onActionVideoReady = () => {
    setActionReady(true);
    setVideoLoading(false);
    void actionVideoRef.current?.play().catch(() => undefined);
  };

  useEffect(() => {
    if (!actionVideoUrl) return undefined;
    const frame = requestAnimationFrame(() => {
      if (!isCreatorChatVideoHot(actionVideoUrl)) return;
      setActionReady(true);
      setVideoLoading(false);
      void actionVideoRef.current?.play().catch(() => undefined);
    });
    return () => cancelAnimationFrame(frame);
  }, [actionVideoUrl]);

  const playReplyAudio = (url?: string | null) => {
    if (!url || !openRef.current) return;
    void playCreatorChatAudioUrl(url, audioRefs()).then((ok) => {
      if (!openRef.current) {
        stopCreatorChatAudio(audioRefs());
        return;
      }
      if (ok) {
        setAudioBlocked(false);
        setPendingAudioUrl(null);
      } else {
        setPendingAudioUrl(url);
        setAudioBlocked(true);
      }
    });
  };

  const unlockAudioGesture = () => {
    unlockCreatorChatAudioGesture(audioRefs());
  };

  const unlockAndPlayAudio = () => {
    const url = pendingAudioUrl;
    if (!url) {
      setAudioBlocked(false);
      return;
    }
    unlockAudioGesture();
    void playCreatorChatAudioUrl(url, audioRefs()).then((ok) => {
      if (ok) {
        setAudioBlocked(false);
        setPendingAudioUrl(null);
      }
    });
  };

  const onActionEnded = () => {
    playedUrlRef.current = null;
    setRingTriggerId(null);
    setActionVideoUrl(null);
    setActionReady(false);
    setShowIdle(true);
    setIdleReady(false);
    setVideoLoading(false);
    window.setTimeout(() => {
      const idle = idleVideoRef.current;
      if (idle) void idle.play().catch(() => undefined);
    }, 40);
  };

  /**
   * Shows the reply as separate messages, pausing before each one.
   *
   * The pauses come from the server so every client paces a reply identically. Awaiting
   * this keeps `busy` true throughout, which both holds the "Typing…" indicator up between
   * bubbles and stops the fan sending again mid-reply.
   */
  const deliverReply = async (res: ChatResponse) => {
    const bubbles = res.bubbles?.length
      ? res.bubbles
      : [{ text: res.reply, delayMs: 0 }];

    for (const bubble of bubbles) {
      if (bubble.delayMs > 0) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, bubble.delayMs));
      }
      // Re-checked each time: the fan can close the widget mid-reply, and the remaining
      // bubbles should not appear when they reopen it.
      if (!openRef.current) return;
      setHistory((h) => [...h, { role: 'assistant', content: bubble.text }]);
    }
  };

  const submitMessage = async (
    message: string,
    opts?: { ringItem?: TriggerRingItem }
  ) => {
    if (!publicId || !message.trim() || busy) return;
    unlockAudioGesture();
    const trimmed = message.trim();
    const prior = history;
    setHistory([...prior, { role: 'user', content: trimmed }]);
    setBusy(true);
    setGeneratingClip(false);
    setAudioBlocked(false);
    setPendingAudioUrl(null);

    if (opts?.ringItem) {
      playActionVideo(opts.ringItem.videoUrl, opts.ringItem.id);
    } else if (config?.actionsEnabled) {
      const instantUrl = matchCreatorChatActionVideo(trimmed, config.actions || []);
      if (instantUrl) {
        playActionVideo(instantUrl);
      }
    }

    try {
      const res = await creatorChatPublicService.chat(
        publicId,
        trimmed,
        prior,
        visitorIdRef.current
      );
      if (!openRef.current) {
        stopChatPlayback();
        return;
      }
      if (res.action?.videoUrl) {
        if (playedUrlRef.current !== res.action.videoUrl) {
          playActionVideo(res.action.videoUrl);
        }
      } else if (res.videoJob?.id) {
        const jobId = res.videoJob.id;
        if (!playedUrlRef.current) {
          setGeneratingClip(true);
          setVideoLoading(true);
        }
        void (async () => {
          const url = await pollChatVideoJob(
            () => creatorChatPublicService.getVideoJob(publicId, jobId),
            {
              onTick: (s) => {
                if (s.status === 'FAILED') setGeneratingClip(false);
              },
            }
          );
          setGeneratingClip(false);
          if (!openRef.current) return;
          if (url) {
            playActionVideo(url);
          } else if (!playedUrlRef.current) {
            setVideoLoading(false);
          }
        })();
      }
      await deliverReply(res);
      if (res.audioUrl && openRef.current) {
        playReplyAudio(res.audioUrl);
      }
    } catch (e: any) {
      setGeneratingClip(false);
      const msg =
        e?.message === '429'
          ? 'Too many messages. Please wait a few minutes.'
          : 'Sorry, something went wrong. Please try again.';
      setHistory((h) => [...h, { role: 'assistant', content: msg }]);
    } finally {
      setBusy(false);
    }
  };

  const playTriggerFromRing = (item: TriggerRingItem) => {
    if (!publicId || busy) return;
    unlockAudioGesture();
    void submitMessage(item.fullLabel, { ringItem: item });
  };

  const send = async () => {
    if (!publicId || !input.trim() || busy) return;
    const message = input.trim();
    setInput('');
    await submitMessage(message);
  };

  if (loading) {
    return (
      <div style={shellStyle}>
        <div style={{ color: '#64748b', fontFamily: 'Nunito, sans-serif' }}>Loading chat…</div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div style={shellStyle}>
        {embedded ? (
          <button
            type="button"
            onClick={() => navigate('/creator-chat')}
            style={{
              alignSelf: 'flex-start',
              marginBottom: 16,
              border: 'none',
              background: 'transparent',
              color: '#086afb',
              fontFamily: 'Mukta, sans-serif',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            ← Back to creators
          </button>
        ) : null}
        <div
          style={{
            maxWidth: 420,
            background: '#fff',
            borderRadius: 16,
            padding: 24,
            boxShadow: '0 10px 40px rgba(15,23,42,0.12)',
            fontFamily: 'Nunito, sans-serif',
            color: '#334155',
            textAlign: 'center',
          }}
        >
          {error || 'Chat not found'}
        </div>
      </div>
    );
  }

  const headerAvatar = !videoEnabled || !showIdle ? (
    config.avatarImageUrl ? (
      <img
        src={config.avatarImageUrl}
        alt=""
        style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.5)' }}
      />
    ) : (
      <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,0.25)' }} />
    )
  ) : null;

  return (
    <div
      className="creator-chat-page-shell"
      style={{ ...shellStyle, ...(shellBackground ? { background: shellBackground } : {}) }}
    >
      {embedded ? (
        <button
          type="button"
          onClick={() => navigate('/creator-chat')}
          style={{
            alignSelf: 'flex-start',
            width: 'min(720px, 100%)',
            marginBottom: 12,
            border: 'none',
            background: 'transparent',
            color: '#086afb',
            fontFamily: 'Mukta, sans-serif',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: 14,
            padding: 0,
          }}
        >
          ← Back to creators
        </button>
      ) : null}
      {!open && !embedded ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed',
            bottom: 24,
            [left ? 'left' : 'right']: 24,
            border: 'none',
            borderRadius: 999,
            padding: '12px 18px',
            background: accent,
            color: '#fff',
            fontWeight: 700,
            fontFamily: 'Nunito, sans-serif',
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          } as React.CSSProperties}
        >
          {config.avatarImageUrl ? (
            <img
              src={config.avatarImageUrl}
              alt=""
              style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }}
            />
          ) : null}
          {config.ctaLabel || 'Chat'}
        </button>
      ) : (
        <div
          className="creator-chat-widget-panel"
          style={{
            width: embedded
              ? 'min(860px, 100%)'
              : wheelVisible
                ? 'min(560px, calc(100vw - 24px))'
                : 'min(420px, calc(100vw - 24px))',
            height: embedded
              ? 'min(720px, calc(100vh - 120px), calc(100dvh - 120px))'
              : 'min(640px, calc(100vh - 24px), calc(100dvh - 24px))',
            maxHeight: embedded ? 'calc(100dvh - 120px)' : 'calc(100dvh - 24px)',
            minHeight: 0,
            background: '#fff',
            borderRadius: 18,
            overflow: 'hidden',
            boxShadow: embedded ? '0 4px 24px rgba(15,23,42,0.1)' : '0 20px 60px rgba(0,0,0,0.35)',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: 'Nunito, Mukta, sans-serif',
          }}
        >
          <div
            className="creator-chat-widget-header"
            style={{
              background: accent,
              color: '#fff',
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexShrink: 0,
            }}
          >
            {headerAvatar}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 16, lineHeight: 1.2 }}>{title}</div>
              {config.presence ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    fontSize: 12,
                    opacity: 0.9,
                    marginTop: 3,
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: config.presence.online ? '#0dca9d' : '#94a3b8',
                      flexShrink: 0,
                    }}
                  />
                  <span>{config.presence.label}</span>
                </div>
              ) : config.subtitle ? (
                <div style={{ fontSize: 12, opacity: 0.9, marginTop: 2 }}>{config.subtitle}</div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={closeChat}
              aria-label={embedded ? 'Back to catalog' : 'Minimize chat'}
              style={{
                border: 'none',
                background: 'rgba(255,255,255,0.2)',
                color: '#fff',
                width: 32,
                height: 32,
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 18,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>

          {videoEnabled ? (
            <div
              className={`creator-chat-widget-stage${
                wheelVisible ? ' creator-chat-widget-stage--with-board' : ''
              }`}
            >
              <div className="creator-chat-widget-media">
              {config.avatarImageUrl ? (
                <img
                  src={config.avatarImageUrl}
                  alt=""
                  className="creator-chat-widget-media-layer"
                  style={{ zIndex: 0 }}
                />
              ) : null}
              {config.idleVideoUrl && showIdle && !actionVideoUrl ? (
                <video
                  ref={idleVideoRef}
                  key={config.idleVideoUrl}
                  src={config.idleVideoUrl}
                  poster={config.avatarImageUrl || undefined}
                  preload="auto"
                  className="creator-chat-widget-media-layer"
                  style={{
                    zIndex: 1,
                    opacity: idleReady ? 1 : 0,
                    transition: 'opacity 120ms linear',
                  }}
                  autoPlay
                  loop
                  muted
                  playsInline
                  onCanPlay={() => {
                    setIdleReady(true);
                    void idleVideoRef.current?.play().catch(() => undefined);
                  }}
                  onPlaying={() => setIdleReady(true)}
                  onError={() => setIdleReady(false)}
                />
              ) : null}
              {actionVideoUrl ? (
                <video
                  ref={actionVideoRef}
                  key={actionVideoUrl}
                  src={actionVideoUrl}
                  poster={config.avatarImageUrl || undefined}
                  preload="auto"
                  className="creator-chat-widget-media-layer creator-chat-widget-media-layer--action"
                  style={{ zIndex: 2, opacity: actionReady ? 1 : 0, transition: 'opacity 80ms linear' }}
                  autoPlay
                  muted
                  playsInline
                  onLoadStart={() => {
                    if (!isCreatorChatVideoHot(actionVideoUrl)) {
                      setVideoLoading(true);
                      setActionReady(false);
                    }
                  }}
                  onLoadedData={onActionVideoReady}
                  onWaiting={() => setVideoLoading(true)}
                  onCanPlay={onActionVideoReady}
                  onPlaying={() => {
                    setActionReady(true);
                    setVideoLoading(false);
                  }}
                  onStalled={() => {
                    void actionVideoRef.current?.play().catch(() => undefined);
                  }}
                  onEnded={onActionEnded}
                />
              ) : null}
              {videoLoading && actionVideoUrl && !actionReady ? (
                <div className="creator-chat-widget-media-overlay" aria-hidden>
                  Loading clip…
                </div>
              ) : null}
              {generatingClip && !actionVideoUrl ? (
                <div className="creator-chat-widget-media-overlay creator-chat-widget-media-overlay--strong">
                  Creating your clip… usually 30–120s
                </div>
              ) : null}
              {!config.avatarImageUrl && !config.idleVideoUrl && !actionVideoUrl ? (
                <div className="creator-chat-widget-media-empty">No avatar or idle video yet</div>
              ) : null}
              </div>

              {wheelVisible ? (
                <aside className="creator-chat-test-dartboard creator-chat-widget-dartboard" aria-label="Trigger phrases">
                  <div className="creator-chat-test-dartboard-wheel">
                    <svg
                      className="creator-chat-test-dartboard-svg"
                      viewBox="0 0 100 100"
                      role="presentation"
                    >
                      {triggerRingItems.map((item, index) => {
                        const n = triggerRingItems.length;
                        const gap = n > 1 ? Math.min(2.5, 18 / n) : 0;
                        const slice = 360 / n;
                        const startDeg = index * slice + gap / 2;
                        const endDeg = (index + 1) * slice - gap / 2;
                        const active = ringTriggerId === item.id;
                        const fill = TRIGGER_RING_COLORS[index % TRIGGER_RING_COLORS.length];
                        return (
                          <path
                            key={`wedge-${item.id}`}
                            d={donutSlicePath(50, 50, 18, 42, startDeg, endDeg)}
                            fill={fill}
                            opacity={active ? 1 : 0.82}
                            className={`creator-chat-test-dartboard-wedge${
                              active ? ' is-active' : ''
                            }`}
                            style={{ cursor: busy ? 'wait' : 'pointer' }}
                            onPointerDown={() => warmCreatorChatVideoOnGesture(item.videoUrl)}
                            onClick={() => playTriggerFromRing(item)}
                          >
                            <title>{item.fullLabel}</title>
                          </path>
                        );
                      })}
                      <circle
                        cx="50"
                        cy="50"
                        r="16.5"
                        fill="#0f172a"
                        className={
                          wheelCenterItem
                            ? `creator-chat-test-dartboard-center-fill${
                                ringTriggerId === wheelCenterItem.id ? ' is-active' : ''
                              }`
                            : undefined
                        }
                      />
                      {!wheelCenterItem ? (
                        <text
                          x="50"
                          y="49"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="#94a3b8"
                          fontSize="5.5"
                          fontWeight="700"
                          style={{ pointerEvents: 'none', userSelect: 'none' }}
                        >
                          Triggers
                        </text>
                      ) : null}
                    </svg>
                    {wheelCenterItem ? (
                      <button
                        type="button"
                        title={wheelCenterItem.fullLabel}
                        aria-label={`Play center clip: ${wheelCenterItem.fullLabel}`}
                        className={`creator-chat-test-dartboard-center${
                          ringTriggerId === wheelCenterItem.id ? ' is-active' : ''
                        }`}
                        disabled={busy}
                        onPointerDown={() => warmCreatorChatVideoOnGesture(wheelCenterItem.videoUrl)}
                        onClick={() => playTriggerFromRing(wheelCenterItem)}
                      >
                        {wheelCenterItem.label}
                      </button>
                    ) : null}
                    {triggerRingItems.map((item, index) => {
                      const n = triggerRingItems.length;
                      const mid = (index + 0.5) * (360 / n);
                      const pos = polarPoint(50, 50, 46.5, mid);
                      const active = ringTriggerId === item.id;
                      const short = truncateTriggerLabel(item.fullLabel, n > 10 ? 8 : 11);
                      return (
                        <button
                          key={`dart-lbl-${item.id}`}
                          type="button"
                          title={item.fullLabel}
                          className={`creator-chat-test-dartboard-label${
                            active ? ' is-active' : ''
                          }`}
                          style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                          disabled={busy}
                          onPointerDown={() => warmCreatorChatVideoOnGesture(item.videoUrl)}
                          onClick={() => playTriggerFromRing(item)}
                        >
                          {short}
                        </button>
                      );
                    })}
                  </div>
                  <p className="creator-chat-widget-dartboard-hint">Tap center or a wedge to play.</p>
                </aside>
              ) : null}
            </div>
          ) : null}

          <div
            ref={messagesRef}
            className="creator-chat-widget-messages"
            style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 14, background: '#f8fafc' }}
          >
            {history.map((t, i) => (
              <div
                key={`${t.role}-${i}`}
                style={{
                  display: 'flex',
                  justifyContent: t.role === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    maxWidth: '82%',
                    padding: '10px 12px',
                    borderRadius: 14,
                    fontSize: 14,
                    lineHeight: 1.4,
                    background: t.role === 'user' ? accent : '#fff',
                    color: t.role === 'user' ? '#fff' : '#0f172a',
                    border: t.role === 'user' ? 'none' : '1px solid #e2e8f0',
                    boxShadow: t.role === 'user' ? 'none' : '0 1px 2px rgba(15,23,42,0.04)',
                  }}
                >
                  {t.content}
                </div>
              </div>
            ))}
            {busy ? <div style={{ fontSize: 12, color: '#94a3b8' }}>Typing…</div> : null}
            <div ref={messagesEndRef} />
          </div>

          <div className="creator-chat-widget-footer">
          {audioBlocked && pendingAudioUrl ? (
            <button
              type="button"
              className="creator-chat-widget-hear-reply"
              onClick={unlockAndPlayAudio}
              style={{
                background: accent,
              }}
            >
              Tap to hear reply
            </button>
          ) : null}

          <div className="creator-chat-widget-composer" style={{ padding: 12, borderTop: '1px solid #e2e8f0', display: 'flex', gap: 8, flexShrink: 0, background: '#fff' }}>
            <input
              className="creator-chat-widget-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onFocus={unlockAudioGesture}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              placeholder={
                videoEnabled
                  ? 'Try "blow me a kiss"…'
                  : voiceEnabled
                    ? 'Write a message — replies can speak…'
                    : 'Write a message…'
              }
              disabled={busy}
              style={{
                flex: 1,
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                padding: '12px 14px',
                fontSize: 14,
                outline: 'none',
              }}
            />
            <button
              type="button"
              className="creator-chat-widget-send"
              onClick={() => void send()}
              disabled={busy || !input.trim()}
              style={{
                border: 'none',
                borderRadius: 12,
                padding: '0 16px',
                background: accent,
                color: '#fff',
                fontWeight: 700,
                cursor: busy || !input.trim() ? 'not-allowed' : 'pointer',
                opacity: busy || !input.trim() ? 0.6 : 1,
              }}
            >
              Send
            </button>
          </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorChatWidgetPage;
